/**
 * Device utilities for managing device IDs and collecting browser information
 * Used for multi-device login support and FCM device registration
 */

import { DevicePlatform } from '../generated/graphql';

/**
 * Generates or retrieves a stable device ID for this browser
 * The ID is stored in localStorage to persist across sessions
 */
export const getOrCreateDeviceId = (): string => {
  const STORAGE_KEY = 'dhs_device_id';

  // Try to get existing device ID from localStorage
  let deviceId = localStorage.getItem(STORAGE_KEY);

  if (!deviceId) {
    // Generate a new device ID using timestamp + random string
    const timestamp = Date.now().toString(36);
    const randomStr = Math.random().toString(36).substring(2, 15);
    deviceId = `web-${timestamp}-${randomStr}`;

    // Store for future sessions
    localStorage.setItem(STORAGE_KEY, deviceId);
  }

  return deviceId;
};

/**
 * Gets the device platform
 * For web applications, this will always be 'WEB'
 */
export const getDevicePlatform = (): DevicePlatform => {
  return DevicePlatform.Web;
};

/**
 * Gets a human-readable device name based on browser and OS
 */
export const getDeviceName = (): string => {
  const userAgent = navigator.userAgent;

  // Detect browser
  let browser = 'Unknown Browser';
  if (userAgent.includes('Firefox/')) {
    browser = 'Firefox';
  } else if (userAgent.includes('Edg/')) {
    browser = 'Edge';
  } else if (userAgent.includes('Chrome/') && !userAgent.includes('Edg/')) {
    browser = 'Chrome';
  } else if (userAgent.includes('Safari/') && !userAgent.includes('Chrome/')) {
    browser = 'Safari';
  } else if (userAgent.includes('OPR/') || userAgent.includes('Opera/')) {
    browser = 'Opera';
  }

  // Detect OS
  let os = 'Unknown OS';
  if (userAgent.includes('Windows NT')) {
    os = 'Windows';
  } else if (userAgent.includes('Mac OS X')) {
    os = 'macOS';
  } else if (userAgent.includes('Linux')) {
    os = 'Linux';
  } else if (userAgent.includes('Android')) {
    os = 'Android';
  } else if (
    userAgent.includes('iOS') ||
    userAgent.includes('iPhone') ||
    userAgent.includes('iPad')
  ) {
    os = 'iOS';
  }

  return `${browser} on ${os}`;
};

/**
 * Gets the device model (for web, this is the browser name and version)
 */
export const getDeviceModel = (): string => {
  const userAgent = navigator.userAgent;

  // Extract browser and version
  const browserRegex = /(Firefox|Chrome|Safari|Edg|OPR|Opera)\/(\d+)/;
  const match = userAgent.match(browserRegex);

  if (match) {
    const browserName = match[1] === 'OPR' ? 'Opera' : match[1];
    const version = match[2];
    return `${browserName} ${version}`;
  }

  return 'Unknown Browser';
};

/**
 * Gets the OS version from user agent
 */
export const getOSVersion = (): string => {
  const userAgent = navigator.userAgent;

  // Windows
  if (userAgent.includes('Windows NT')) {
    const match = userAgent.match(/Windows NT (\d+\.\d+)/);
    if (match) {
      const version = match[1];
      // Map NT versions to Windows versions
      const versionMap: Record<string, string> = {
        '10.0': 'Windows 10/11',
        '6.3': 'Windows 8.1',
        '6.2': 'Windows 8',
        '6.1': 'Windows 7',
      };
      return versionMap[version] || `Windows NT ${version}`;
    }
    return 'Windows';
  }

  // macOS
  if (userAgent.includes('Mac OS X')) {
    const match = userAgent.match(/Mac OS X (\d+[._]\d+([._]\d+)?)/);
    if (match) {
      return `macOS ${match[1].replace(/_/g, '.')}`;
    }
    return 'macOS';
  }

  // Linux
  if (userAgent.includes('Linux')) {
    return 'Linux';
  }

  // Android
  if (userAgent.includes('Android')) {
    const match = userAgent.match(/Android (\d+(\.\d+)?)/);
    if (match) {
      return `Android ${match[1]}`;
    }
    return 'Android';
  }

  // iOS
  if (userAgent.includes('OS ')) {
    const match = userAgent.match(/OS (\d+_\d+(_\d+)?)/);
    if (match) {
      return `iOS ${match[1].replace(/_/g, '.')}`;
    }
    return 'iOS';
  }

  return 'Unknown OS';
};

/**
 * Gets the app version from package.json or environment variable
 */
export const getAppVersion = (): string => {
  // Try to get from environment variable first
  const envVersion = import.meta.env.VITE_APP_VERSION;
  if (envVersion) {
    return envVersion;
  }

  // Default version
  return '1.0.0';
};

/**
 * Gets the user's locale
 */
export const getUserLocale = (): string => {
  return navigator.language || 'en-US';
};

/**
 * Gets the user's timezone
 */
export const getUserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
};

/**
 * Collects all device information needed for device registration
 */
export interface DeviceInfo {
  platform: DevicePlatform;
  deviceId: string;
  deviceName: string;
  deviceModel: string;
  osVersion: string;
  appVersion: string;
  locale: string;
  timezone: string;
  fcmToken?: string;
}

/**
 * Gets complete device information for registration
 * @param fcmToken Optional Firebase Cloud Messaging token
 */
export const getDeviceInfo = (fcmToken?: string): DeviceInfo => {
  const deviceInfo: DeviceInfo = {
    platform: getDevicePlatform(),
    deviceId: getOrCreateDeviceId(),
    deviceName: getDeviceName(),
    deviceModel: getDeviceModel(),
    osVersion: getOSVersion(),
    appVersion: getAppVersion(),
    locale: getUserLocale(),
    timezone: getUserTimezone(),
  };

  if (fcmToken) {
    deviceInfo.fcmToken = fcmToken;
  }

  return deviceInfo;
};

/**
 * Clears the stored device ID (useful for testing or logout)
 */
export const clearDeviceId = (): void => {
  localStorage.removeItem('dhs_device_id');
};
